'use client';

import type { FC } from 'react';
import { ScreenContent, ScreenDisableSafeArea } from '../../screen';
import { Skeleton, SegmentedControl } from '../../components/ui';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations from './LevelInfoScreen.translations';

export const LevelInfoScreenSkeleton: FC = () => {
  const { t } = useLocalTranslation(translations);
  const faqs = [
    { q: t('faqQ1'), a: t('faqA1') },
    { q: t('faqQ2'), a: t('faqA2') },
    { q: t('faqQ3'), a: t('faqA3') },
    { q: t('faqQ4'), a: t('faqA4') },
  ];

  return (
    <>
      <ScreenDisableSafeArea />
      <ScreenContent>
        <div className="px-4 pb-6">
          <div className="flex flex-col items-center py-6 pt-[calc(24px+var(--tg-viewport-content-safe-area-inset-top,0px)+var(--tg-viewport-safe-area-inset-top,0px))]">
            <div className="relative mb-3">
              <Skeleton className="w-[96px] h-[96px] rounded-full" />
              <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-fill-tertiary" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <Skeleton className="h-7 w-24" />
              <Skeleton className="h-5 w-32" />
            </div>
          </div>

          <div className="flex flex-col gap-2 mb-6">
            <div className="rounded-3xl bg-bg-secondary p-4 flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <Skeleton className="h-[14px] w-24" />
                <Skeleton className="h-[14px] w-14" />
              </div>
              <div className="h-2 bg-fill-tertiary rounded-full overflow-hidden" />
            </div>
            <Skeleton className="h-[44px] w-full rounded-full" />
          </div>

          <div className="mb-6">
            <h3 className="text-label-primary text-xl font-bold mb-3">{t('howItWorksTitle')}</h3>
            <div className="rounded-2xl bg-bg-secondary p-4 flex flex-col gap-3">
              {[
                t('howItWorksFeature1', { maxLevel: 100 }),
                t('howItWorksFeature2'),
                t('howItWorksFeature3'),
                t('howItWorksFeature4'),
              ].map((text, i) => (
                <p key={i} className="m-0 text-label-secondary text-[14px] leading-snug">{text}</p>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-label-primary text-xl font-bold mb-3">{t('keyLevels')}</h3>
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-2xl bg-bg-secondary p-3 flex items-center gap-3">
                  <Skeleton className="!w-11 !h-11 shrink-0 rounded-full" />
                  <div className="flex-1 min-w-0">
                    <Skeleton className="h-[15px] w-20" />
                    <Skeleton className="mt-1 h-3 w-14" />
                  </div>
                  <Skeleton className="h-9 w-16 shrink-0 rounded-full" />
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-bold text-white m-0 mb-3">{t('xpSourcesTitle')}</h2>
            <div className="mb-4">
              <SegmentedControl<'activity' | 'gameplay' | 'social'>
                segments={[
                  { id: 'activity', label: t('tabActivity') },
                  { id: 'gameplay', label: t('tabGameplay') },
                  { id: 'social', label: t('tabSocial') },
                ]}
                value="activity"
                onChange={() => {}}
                className="px-4 py-2"
              />
            </div>
            <div className="rounded-2xl bg-bg-secondary overflow-hidden">
              {Array.from({ length: 4 }).map((_, i, arr) => (
                <div key={i}>
                  <div className="flex items-center justify-between gap-3 px-4 py-3">
                    <Skeleton className="h-[18px] w-40" />
                    <Skeleton className="h-[18px] w-12 shrink-0" />
                  </div>
                  {i < arr.length - 1 && <div className="h-px bg-separator-non-opaque ms-4" />}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-label-primary text-xl font-bold mb-3">{t('faqTitle')}</h2>
            <div className="rounded-2xl bg-bg-secondary overflow-hidden">
              {faqs.map((item, idx, arr) => (
                <div key={idx}>
                  <div className="w-full flex items-center justify-between gap-3 px-4 py-3 text-start">
                    <span className="text-label-primary text-[15px] font-medium leading-tight">{item.q}</span>
                  </div>
                  {idx < arr.length - 1 && <div className="h-px bg-separator-non-opaque ms-4" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </ScreenContent>
    </>
  );
};
